// Deeplink Landing Page — helper utilities
import {
  PLATFORMS,
  PUBLIC_DESTINATIONS,
  STORE_URLS,
  APP_SCHEME,
} from "./constants";

/**
 * Detect client platform from the User-Agent string.
 * Reference: Section 7.4 — Step 1
 */
export const detectPlatform = (userAgent = navigator.userAgent || "") => {
  const ua = userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return PLATFORMS.IOS;
  // iPad on iOS 13+ reports as Mac — additional touch check
  if (/macintosh/.test(ua) && navigator.maxTouchPoints > 1) {
    return PLATFORMS.IOS;
  }
  if (/android/.test(ua)) return PLATFORMS.ANDROID;
  return PLATFORMS.DESKTOP;
};

/**
 * Get the base URL for the deeplink microservice.
 * Reads from REACT_APP_API_URL and appends /deeplink/v1.
 */
export const getDeeplinkApiBase = () => {
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  return `${base}/deeplink/v1`;
};

// Allowlist of supported deeplink query keys. Anything else from the URL is
// dropped so that user-controlled keys cannot reach a property assignment —
// this is the recommended mitigation for CodeQL `js/remote-property-injection`.
// Keep this in sync with consumers in index.js / ScreenDesktopPublic.js.
const ALLOWED_QUERY_KEYS = [
  "destination",
  "user_state",
  "lang",
  "ref_id",
  // Content identifiers — without these the app opens the destination with no
  // target (e.g. Fund Info with no fund), see CONTENT_PARAMS below.
  "fundCode",
  "FundNo",
  "feedId",
  "articleId",
];

// Which content identifier each destination carries, mapped from the key used
// on the landing URL to the key the app's linking config parses.
// App side (mobile RootRoute.tsx):
//   fund-info            parses ?fundCode (preferred) or ?FundNo
//   feed / alias article parses ?feedId
// `articleId` is accepted as a legacy alias for `feedId` so older links keep
// working; the canonical key always wins.
const CONTENT_PARAMS = {
  fund_info: { fundCode: "fundCode", FundNo: "FundNo" },
  article: { feedId: "feedId", articleId: "feedId" },
};

// Destinations whose app route is not just the kebab-cased destination.
//
// forgot_credentials: TDD Appendix A lists `cpamapp://forgot-credentials`, but
// that path opens PVDMigrateForgotUsernamePasswordIndex — the PVD migrate page,
// not this journey. The screen designed for this flow (Figma 7040:223679) is the
// app's `forgot-password` route, which branches on device state: logged-in or
// subsequent-login gets the switch-account popup, a true guest gets verify-email.
const SCHEME_PATH_OVERRIDES = {
  forgot_credentials: "forgot-password",
};

// Own-property lookup only — `destination` comes from the URL, so a plain index
// could otherwise reach inherited members (CodeQL js/remote-property-injection).
const schemePathFor = destination =>
  (Object.prototype.hasOwnProperty.call(SCHEME_PATH_OVERRIDES, destination)
    ? SCHEME_PATH_OVERRIDES[destination]
    : destination.replace(/_/g, "-"));

/**
 * Parse the query string from window.location.search into a plain object.
 *
 * Only keys in ALLOWED_QUERY_KEYS are copied through. The result has a null
 * prototype so even if the allowlist is later widened, prototype pollution
 * via keys like `__proto__` cannot occur.
 */
export const parseQuery = (search = window.location.search) => {
  const out = Object.create(null);
  if (!search || search.length <= 1) return out;
  const params = new URLSearchParams(search);
  for (let i = 0; i < ALLOWED_QUERY_KEYS.length; i += 1) {
    const key = ALLOWED_QUERY_KEYS[i];
    const value = params.get(key);
    if (value !== null) {
      out[key] = value;
    }
  }
  return out;
};

/**
 * Extract the destination identifier from either:
 *  - the query string `?destination=open_mf` (preferred)
 *  - the path segment `/deeplink/landing/open_mf` (fallback)
 */
export const extractDestination = (
  search = window.location.search,
  pathname = window.location.pathname
) => {
  const q = parseQuery(search);
  if (q.destination) return q.destination;
  const segments = pathname.split("/").filter(Boolean);
  // Path: /deeplink/landing/<destination>
  if (segments[0] === "deeplink" && segments[1] === "landing" && segments[2]) {
    return segments[2];
  }
  return null;
};

/**
 * Determine if a destination is public (Fund Tab/Info/Article).
 * Public destinations get Screen B (with web fallback).
 * Private destinations get Screen C (download only).
 */
export const isPublicDestination = destination =>
  PUBLIC_DESTINATIONS.includes(destination);

/**
 * Build the app scheme URL for a destination.
 *
 * `query` is the parsed landing-page query string; any content identifier it
 * carries for this destination is forwarded, otherwise the app would open the
 * destination with nothing to show.
 *
 * Examples:
 *   open_mf                              -> cpamapp://open-mf
 *   fund_info + {fundCode: 'TREASURY'}   -> cpamapp://fund-info?fundCode=TREASURY
 *   article   + {feedId: 'abc'}          -> cpamapp://article?feedId=abc
 *   forgot_credentials                   -> cpamapp://forgot-password (see overrides)
 */
export const buildAppSchemeUrl = (destination, refId, lang, query = {}) => {
  // destinations use snake_case in the API but kebab-case in the scheme
  // e.g. open_mf -> open-mf, soft_onboard -> soft-onboard
  const url = new URL(`${APP_SCHEME}${schemePathFor(destination)}`);

  // Keys come from the CONTENT_PARAMS literal, never from the URL, so this
  // stays clear of CodeQL `js/remote-property-injection`.
  const mapping = CONTENT_PARAMS[destination] || {};
  Object.keys(mapping).forEach(fromKey => {
    const appKey = mapping[fromKey];
    const value = query[fromKey];
    // First match wins so a legacy alias can't overwrite the canonical key.
    if (value && !url.searchParams.has(appKey)) {
      url.searchParams.set(appKey, value);
    }
  });

  if (refId) url.searchParams.set("ref", refId);
  if (lang) url.searchParams.set("lang", lang);
  return url.toString();
};

/**
 * Get the store URL for the user's platform.
 */
export const getStoreUrl = platform => {
  if (platform === PLATFORMS.IOS) return STORE_URLS.IOS;
  if (platform === PLATFORMS.ANDROID) return STORE_URLS.ANDROID;
  return STORE_URLS.IOS; // sensible default for desktop fallback
};

/**
 * Trigger the cpamapp:// URL scheme to attempt opening the app.
 *
 * Only called on the open_app / open_app_login_first path (i.e. when /resolve
 * has determined the app IS installed, or as a best-effort default when the
 * backend is unavailable). When the app is NOT installed the caller routes
 * straight to the store via the redirect_store action and never reaches here —
 * this avoids the iOS Safari "cannot open the page" error that any unhandled
 * custom-scheme navigation (window.location OR iframe) raises on modern iOS.
 *
 * MEASURED (iPhone 17 simulator, iOS 26, 2026-09-03): iOS Safari raises a
 * blocking confirm dialog for a custom scheme whether or not a user gesture is
 * behind the navigation — only the wording differs ("Open this page in
 * 'Principal TH'?" when automatic, "Open in 'Principal TH'?" from a tap). So no
 * client-side arrangement makes this silent, and nothing on the page may assume
 * the tab has been backgrounded just because the attempt was made. Avoiding the
 * dialog altogether needs a Universal Link rather than a custom scheme. Android
 * shows no prompt either way.
 */
export const triggerAppScheme = url => {
  window.location.assign(url);
};
